import React, {useMemo} from 'react'
import {Button, Grid, Typography} from '@mui/material'
import LocationCardRow from './LocationCardRow/LocationCardRow';
import {
  LocationCardBuyerCardMarginTop,
  LocationCardContainer,
  LocationCardDeactiveButton,
  LocationCardEndBtn,
  LocationCardGap,
  LocationCardHeader,
  LocationCardHeaderTitle,
  LocationCardImgStyles,
  LocationCardLabel,
  LocationCardMarginTop,
  LocationCardPhoneIcon,
  LocationCardRoomIcon,
  LocationCardStatusContainer,
  LocationCardValue
} from './styles';
import {ILocation} from './interfaces';
import {ITransformedData, transformKey} from "../InfoCard/InfoCard";

/**
 * Old Location Card Row
 * @param label
 * @param value
 * @deprecated
 */
const Row = ({label, value}: { label: string, value: string | number }) => {
  return <Grid xs={12} sm={6} md={3} item>
    <LocationCardValue variant='body1'>{value}</LocationCardValue>
    <LocationCardLabel variant='body2'>
      {label}
    </LocationCardLabel>
  </Grid>
}

/**
 * Old Location Card
 * @param onDeactivate
 * @param onEdit
 * @param data
 * @param rootStyle
 * @param headerStyle
 * @param isBuyer
 * @param buttons
 * @param headCells
 * @param IconButton
 * @param imageAlt
 * @param showAddress
 * @deprecated
 */
const LocationCard = ({
                        onDeactivate,
                        onEdit,
                        data,
                        rootStyle,
                        headerStyle,
                        isBuyer = false,
                        buttons,
                        headCells,
                        IconButton,
                        imageAlt = "Location Image",
                        showAddress = true,
                        statusChip
                      }: ILocation) => {

  const transformedUpperSectionData = useMemo(() => {
    const upperSectionDataKeys = headCells?.slice(0, 1).map(cell => cell.id);
    const transformedData: ITransformedData = {};
    upperSectionDataKeys?.forEach(key => {
      const transformedKey = transformKey(key);
      transformedData[transformedKey] = {
        value: data[key] || '',
        label: headCells?.find(cell => cell.id === key)?.label || ''
      };
    });
    return transformedData;
  }, [data, headCells]);

  const transformedSecondRowData = useMemo(() => {
    const subRowDataKeys = headCells?.slice(1, 2).map(cell => cell.id);
    const transformedData: ITransformedData = {};
    subRowDataKeys?.forEach(key => {
      const transformedKey = transformKey(key);
      transformedData[transformedKey] = {
        value: data[key] || '',
        label: headCells?.find(cell => cell.id === key)?.label || ''
      };
    });
    return transformedData;
  }, [data, headCells]);

  const transformedThirdRowData = useMemo(() => {
    const subRowDataKeys = headCells?.slice(2).map(cell => cell.id);
    const transformedData: ITransformedData = {};
    subRowDataKeys?.forEach(key => {
      const transformedKey = transformKey(key);
      transformedData[transformedKey] = {
        value: data[key] || '',
        label: headCells?.find(cell => cell.id === key)?.label || ''
      };
    });
    return transformedData;
  }, [data, headCells]);

  const imageSrc = `https://s3-us-west-1.amazonaws.com/securspace-files/listing-images/${data?.listingImageFileName}`;

  return (
    <LocationCardContainer item className={rootStyle}>
      <LocationCardHeader className={headerStyle} container justifyContent='space-between'
            alignItems='center'>
        <Grid item>
          <LocationCardHeaderTitle variant='h6'>{data?.locationName || 'N/A'}</LocationCardHeaderTitle>
          {
            !isBuyer &&
            <LocationCardStatusContainer item isActive={data?.locationStatus === "Active"}>
              <Typography variant='body2'>{data?.locationStatus || 'N/A'}</Typography>
            </LocationCardStatusContainer>
          }
          {statusChip}
        </Grid>
        {
          !isBuyer && onEdit && onDeactivate ?
            <Grid item>
              <LocationCardEndBtn onClick={() => onEdit(data)} variant='text'>
                edit
              </LocationCardEndBtn>
              <LocationCardDeactiveButton onClick={() => onDeactivate(data)} variant='outlined'>
                deactivate
              </LocationCardDeactiveButton>
            </Grid>
            : buttons ?
              buttons.map((button) => (
                <Button
                  onClick={() => button.onClick(data)}
                  variant={button.variant || "outlined"}
                  sx={button.styles}
                  key={button.label}
                >
                  {button.label}
                </Button>
              ))
              : IconButton ? < IconButton/> : ''
        }
      </LocationCardHeader>
      <Grid spacing={2} container>
        <Grid item xs={12} md={4}>
          <LocationCardImgStyles
            src={imageSrc}
            imgAlt={imageAlt}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          {
            !isBuyer ?
              <LocationCardMarginTop spacing={2} container alignItems='center'>
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                { /** @ts-expect-error */}
                <Row label='Search Status:' value={data?.isVisible ? 'Visible' : 'Not Visible' || 'N/A'}/>
                <Row label='Number Of Space:' value={data?.managedSpaces || 'N/A'}/>
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                { /** @ts-expect-error */}
                <Row label='Automated Management:' value={data?.managedLocation ? 'Yes' : 'No' || 'N/A'}/>
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                { /** @ts-expect-error */}
                <Row label='QR Scanner Available:' value={data?.qrScannerAvailable ? 'Yes' : 'No' || 'N/A'}/>
                <Row label='Per Day:' value={`$${data?.pricePerDay}`}/>
                <Row label='Per Month:' value={`$${data?.pricePerMonth}`}/>
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                { /** @ts-expect-error */}
                <Row label='Charge Overages:' value={data?.chargeOverages ? 'Yes' : 'No' || 'N/A'}/>
                <Row label='Overage Rate:' value={data?.overageRate ? `$${data?.overageRate}` : 'N/A'}/>
              </LocationCardMarginTop>
              : <LocationCardBuyerCardMarginTop
                spacing={6}
                container
                flexDirection="column"
              >
                <Grid item container>
                  {Object.entries(transformedUpperSectionData).map(([key, {value, label}]) => (
                    <LocationCardRow
                      label={label}
                      value={value?.toString() || "N/A"}
                      key={key}
                    />
                  ))}
                </Grid>
                <Grid item container>
                  {Object.entries(transformedSecondRowData).map(([key, {value, label}]) => (
                    <LocationCardRow
                      label={label}
                      value={value?.toString() || "N/A"}
                      key={key}
                    />
                  ))}
                </Grid>
                <Grid item container rowGap={2}>
                  {Object.entries(transformedThirdRowData).map(([key, {value, label}]) => (
                    <LocationCardRow
                      label={label}
                      value={value?.toString() || "N/A"}
                      md={4}
                      key={key}
                    />
                  ))}
                </Grid>
              </LocationCardBuyerCardMarginTop>
          }
          {
            showAddress &&
            <LocationCardMarginTop spacing={2} container alignItems='center' justifyContent='space-between'>
              <Grid xs={12} item>
                <LocationCardGap wrap='nowrap' container alignItems='center'>
                  <LocationCardRoomIcon />
                  <LocationCardValue variant='body1'>
                    {`${data?.addressLine1} ${data?.city}, ${data?.state}` || 'N/A'}
                  </LocationCardValue>
                </LocationCardGap>
              </Grid>
            </LocationCardMarginTop>
          }
          {
            !isBuyer ?
              <Grid xs={12} sm={6} md={3} item>
                <LocationCardGap container alignItems='center'>
                  <LocationCardPhoneIcon />
                  <LocationCardValue variant='body1'>
                    {data?.phoneNumber || 'N/A'}
                  </LocationCardValue>
                </LocationCardGap>
              </Grid>
              : ""
          }
        </Grid>
      </Grid>
    </LocationCardContainer>
  )
}

export default LocationCard;
